import HBIcon from "@/components/HBIcon";
import HBSwiper from "@/components/HBSwiper";
import Image from "@/components/Image";
import Cover, {
  ImageMargin,
  Radius,
} from "@/pageComponents/MaterialCalendar/cover";
import { getTopicsPreview } from "@/services";
import { putFirst } from "@/utils/arr";
import { gdImgSrcPolicy } from "@/utils/imageUrl";
import { useCreation, useSafeState, useUpdateEffect } from "ahooks";
import classNames from "classnames";
import { shuffle } from "lodash";
import { useState } from "react";
import styles from "./index.module.less";

interface RecommendTopic {
  urlname?: string;
  filter_id: string;
  preview: {
    pin_count: number;
    pins: IPin[];
  };

  origin: { title: string };

  filter: {
    name: string;
    id: number;
  };

  order: number;
  link: string;
  title?: string;
}

interface RecommendTopicsProps {
  searchWord: string;
  titleType: "image" | "muti";
  titleImage?: string;
  iconImage?: string;
  title?: string;
  subTitle?: string;

  recommend: {
    type: "code" | "link";
    order: number;
    name: string;
    code?: string;
    recommend?: RecommendTopic[];
  }[];
}

// 随机主题封面
function randomTopicCover(data: RecommendTopicsProps["recommend"]["0"]) {
  data.recommend.forEach((topic) => {
    topic.preview.pins = shuffle(topic.preview.pins);
  });

  return data;
}

/**搜索推荐主题组件 */
export default function RecommendTopics({
  searchWord,
  topics,
  onClose,
}: {
  searchWord: string;
  topics: RecommendTopicsProps;
  onClose: () => void;
}) {
  const {
    titleType,
    titleImage,
    iconImage,
    title,
    subTitle = "高质量素材，商用 0 风险",
    recommend = [],
  } = topics || {};

  /** 过滤没有筛选器和预览封面图片为空的, 同时将符合匹配搜索词内容提前: 相等放最前面*/
  const filterDataSource = (data: RecommendTopic[]) => {
    return putFirst(
      putFirst(
        data.filter((item) => item.filter && item.preview?.pins?.length),
        (item) => item.filter.name.includes(searchWord)
      ),
      (item) => item.filter.name === searchWord
    );
  };

  const searchTitle = title || searchWord;

  // 第一次渲染时随机封面
  useCreation(() => {
    randomTopicCover(recommend[0]);
  }, []);

  const [current, setCurrent] = useState(recommend[0]);
  const [dataSource, setDataSource] = useSafeState(
    filterDataSource(recommend[0]?.recommend || [])
  );

  // 搜索其他内容时，推荐内容更新，重置current
  useUpdateEffect(() => {
    setCurrent(randomTopicCover(recommend[0]));
  }, [recommend]);

  useUpdateEffect(() => {
    // 第一个搜索时已经默认返回了，不需要再去查询
    if (current === recommend[0]) {
      setDataSource(filterDataSource(recommend[0].recommend));
      return;
    }

    // 查询主题预览信息
    getTopicsPreview({
      preview_limit: 4,
      filter_ids: current.recommend?.map((filter) => filter.filter_id),
    }).then((res) => {
      const dataSource = filterDataSource(res.filters).map((filter) => {
        const recommend =
          current.recommend.find(
            (item) => item.filter_id === filter.filter_id
          ) || {};
        return {
          ...recommend,
          ...filter,
        };
      });
      setDataSource(dataSource);
    });
  }, [current]);

  const RecommendTopicCard = Cover;

  return (
    <div className={styles.recommend}>
      <div className={styles.header}>
        {titleType === "image" ? (
          <Image
            {...gdImgSrcPolicy(titleImage, { displayHeight: 36 })}
            alt={searchTitle}
            height={36}
          />
        ) : (
          <div className={styles.left}>
            {iconImage && (
              <Image
                {...gdImgSrcPolicy(iconImage, { displayHeight: 36 })}
                alt={searchTitle}
                height={36}
              />
            )}
            {searchTitle && <span className={styles.title}>{searchTitle}</span>}
            {subTitle && <span className={styles.subTitle}>{subTitle}</span>}
          </div>
        )}

        <div className={styles.tabs}>
          {recommend?.length > 1 &&
            recommend.map((item) => {
              return (
                <span
                  key={item.name}
                  className={classNames(styles.item, {
                    [styles.selected]: item === current,
                  })}
                  onMouseEnter={() => {
                    setCurrent(item);
                  }}
                >
                  {item.name}
                </span>
              );
            })}
        </div>
      </div>

      <HBIcon
        type="ic_close"
        className={styles.close}
        onClick={onClose}
        data-gd-click="button_click"
        data-button-name="关闭推荐主题"
        data-content-source="推荐主题"
      />
      <div>
        <HBSwiper className={styles.swipper}>
          {dataSource.map((item, index) => {
            return (
              <RecommendTopicCard
                title={item?.origin?.title}
                radius={Radius.outer}
                margin={ImageMargin.small}
                key={index}
                pinCount={item.preview.pin_count}
                pins={item.preview.pins}
                filter={item.filter}
                link={
                  item.urlname
                    ? `/topics/${item.urlname}-${item.filter_id}`
                    : item.link
                }
                data-gd-expose="topic_expose"
                data-gd-click="topic_click"
                data-topic-id={item.filter.id}
                data-topic-type="二级主题"
                data-topic-title={item.filter.name}
                data-content-source={`推荐主题_${current?.name}_${item.filter.name}`}
              />
            );
          })}
        </HBSwiper>
      </div>
    </div>
  );
}
